import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollTop from "@/components/feature/ScrollTop";
import TopicCard from "@/components/feature/TopicCard";
import RankSidebar from "@/components/feature/RankSidebar";
import NotFound from "@/pages/NotFound";
import { channels, getChannel } from "@/mocks/channels";
import { topics } from "@/mocks/topics";
import { useSeo } from "@/hooks/useSeo";

export default function Channel() {
  const { slug = "" } = useParams();
  const channel = getChannel(slug);
  const [sort, setSort] = useState<"heat" | "latest">("heat");

  useSeo({
    title: channel
      ? `${channel.name}频道 - 网友热议与舆论风向 | 全网吃瓜`
      : "频道不存在 | 全网吃瓜",
    description: channel
      ? `全网吃瓜「${channel.name}」频道：${channel.description}${channel.tagline}，围观最新热门话题的网友评论与舆论风向。`
      : "页面不存在或已被移除，返回首页继续围观吧。",
  });

  const list = useMemo(() => {
    const arr = topics.filter((t) => t.channel === slug);
    if (sort === "heat") arr.sort((a, b) => b.heat - a.heat);
    else arr.sort((a, b) => b.comments - a.comments);
    return arr;
  }, [slug, sort]);

  if (!channel) {
    return <NotFound />;
  }

  const otherChannels = channels.filter((c) => c.slug !== channel.slug);
  const rankChip =
    channel.accent === "primary"
      ? "bg-primary-500 text-background-50"
      : channel.accent === "accent"
        ? "bg-accent-500 text-background-50"
        : "bg-secondary-600 text-background-50";

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* 频道横幅 */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={channel.image} alt={`${channel.name}频道横幅`} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/55 to-foreground-950/25" />
          </div>
          <div className="wrap relative py-14 md:py-20">
            <nav aria-label="面包屑" className="text-sm text-background-200/90">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="hover:text-background-50">
                    首页
                  </Link>
                </li>
                <li>/</li>
                <li className="text-background-100">{channel.name}</li>
              </ol>
            </nav>
            <span className={`mt-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${rankChip}`}>
              <i className={channel.icon} aria-hidden="true" /> {channel.tagline}
            </span>
            <h1 className="mt-3 font-display text-4xl font-bold text-background-50 md:text-5xl">{channel.name}</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
              {channel.description}
            </p>
            <p className="mt-5 text-sm font-medium text-background-50">
              本频道正在热议 <span className="font-bold">{list.length}</span> 个话题
            </p>
          </div>
        </section>

        {/* 内容区 */}
        <div className="wrap mt-10 grid gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-foreground-900">
                <i className="ri-chat-smile-3-line text-primary-500" aria-hidden="true" />
                频道热议
              </h2>
              <div className="flex gap-2">
                {[
                  { key: "heat" as const, label: "按热度" },
                  { key: "latest" as const, label: "按讨论量" },
                ].map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setSort(o.key)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      sort === o.key
                        ? "bg-primary-500 font-bold text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {list.length === 0 ? (
              <div className="card-tint rounded-2xl p-10 text-center text-sm text-foreground-500">
                这个频道还没有收录话题，去别的频道看看～
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2">
                {list.map((t) => (
                  <TopicCard key={t.slug} topic={t} />
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <RankSidebar activeSlug={slug} />
            <div className="card-tint rounded-2xl p-4">
              <h3 className="mb-3 text-base font-bold text-foreground-900">更多频道</h3>
              <div className="grid grid-cols-1 gap-2">
                {otherChannels.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/channels/${c.slug}`}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                  >
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-lg text-base ${
                        c.accent === "primary"
                          ? "bg-primary-100 text-primary-700"
                          : c.accent === "accent"
                            ? "bg-accent-100 text-accent-700"
                            : "bg-secondary-200 text-secondary-800"
                      }`}
                    >
                      <i className={c.icon} aria-hidden="true" />
                    </span>
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
      <ScrollTop />
    </div>
  );
}